import {useEffect} from 'react';
import {useLocation} from 'react-router';

/**
 * Keeps the floating chat launcher clear of a page's own bottom bar.
 *
 * On a phone the launcher sat exactly on top of the last thing a page put at
 * the bottom — the dashboard's tab bar, and an opportunity's "apply" bar — so
 * the button underneath could not be seen or pressed (owner, 2026-10-01).
 *
 * Any bar that owns the bottom of the screen marks itself with
 * `data-page-bottom-bar`; this measures the tallest visible one and publishes
 * it as `--lc-lift` on <html>. livechat-loader.js adds that to the launcher's
 * offset, so nothing has to know which pages have a bar — a new one is handled
 * the day it adds the attribute.
 */
export function useLauncherLift() {
  const {pathname} = useLocation();

  useEffect(() => {
    const root = document.documentElement;

    const measure = () => {
      let tallest = 0;
      document.querySelectorAll<HTMLElement>('[data-page-bottom-bar]').forEach(el => {
        // A bar that is hidden at this width (display:none) measures 0.
        const height = el.offsetHeight;
        if (height > 0 && getComputedStyle(el).visibility !== 'hidden') {
          tallest = Math.max(tallest, height);
        }
      });

      const lift = tallest ? tallest + 8 : 0;
      if (root.style.getPropertyValue('--lc-lift') !== `${lift}px`) {
        root.style.setProperty('--lc-lift', `${lift}px`);
        // The loader lives outside React and only repositions on its own
        // events; tell it something moved.
        window.dispatchEvent(new Event('lc:reposition'));
      }
    };

    // The bar usually renders after the page's data arrives, so measure again
    // a moment later rather than only on mount.
    measure();
    const timers = [120, 600, 1500].map(t => setTimeout(measure, t));

    const observer = new ResizeObserver(() => measure());
    document.querySelectorAll<HTMLElement>('[data-page-bottom-bar]').forEach(el => observer.observe(el));
    window.addEventListener('resize', measure);

    return () => {
      timers.forEach(clearTimeout);
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [pathname]);
}
